import { Injectable } from '@angular/core';
import { SettingsRepository } from '@app/store';
import { ModalController } from '@ionic/angular/standalone';
import { firstValueFrom } from 'rxjs';

import { LoggingService } from '../logging/logging.service';

/**
 * Shows the "welcome to your stay" sheet modal the first time a user
 * lands on the home or explore page (anonymous or authenticated users).
 *
 * The "shown" state is persisted via `SettingsRepository` (Elf store with
 * localStorage strategy) — the same mechanism the planner onboarding uses
 * — so the modal never re-appears once dismissed.
 */
@Injectable({
  providedIn: 'root',
})
export class WelcomeOnboardingService {
  public readonly showWelcomeOnboarding$ =
    this.settingsRepository.showWelcomeOnboarding$;

  private opening = false;

  constructor(
    private readonly modalCtrl: ModalController,
    private readonly settingsRepository: SettingsRepository,
  ) {}

  public getShowWelcomeOnboarding(): boolean {
    return this.settingsRepository.getShowWelcomeOnboarding();
  }

  public setShowWelcomeOnboarding(show: boolean): void {
    this.settingsRepository.setShowWelcomeOnboarding(show);
  }

  /**
   * Presents the welcome onboarding modal once if it has never been shown
   * on this device. Safe to call from multiple pages — only the first call
   * actually opens the modal.
   */
  public async tryShow(): Promise<void> {
    if (this.opening) {
      return;
    }

    // Wait until the persisted store has hydrated so the flag value is real.
    await firstValueFrom(this.settingsRepository.initialized$);

    if (!this.getShowWelcomeOnboarding()) {
      return;
    }

    this.opening = true;

    try {
      // Lazy-import the modal to keep the caller's initial bundle small.
      const { WelcomeOnboardingModalComponent } = await import(
        '@app/shared/modals/welcome-onboarding/welcome-onboarding.modal'
      );

      const modal = await this.modalCtrl.create({
        component: WelcomeOnboardingModalComponent,
        breakpoints: [0, 1],
        initialBreakpoint: 1,
        cssClass: ['modal-height-auto', 'welcome-onboarding-modal'],
        backdropDismiss: true,
      });

      await modal.present();
      await modal.onDidDismiss();

      // Persist that the user has dismissed the onboarding sheet so it
      // never re-appears on subsequent app launches.
      this.setShowWelcomeOnboarding(false);
    } catch (error) {
      LoggingService.log('WelcomeOnboardingService: tryShow failed', error);
    } finally {
      this.opening = false;
    }
  }
}
